import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import {
  Backdrop,
  CircularProgress,
  createTheme,
  CssBaseline,
  StyledEngineProvider,
  ThemeProvider,
} from '@mui/material';
import React, { Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from 'react-router-dom';
import rtlPlugin from 'stylis-plugin-rtl';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminLayout from './components/layout/AdminLayout';
import { AuthProvider } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
import AdminProducts from './pages/admin/AdminProducts';
import { renderRoutes } from './routes';
import theme from './theme';

// Public components
import Footer from './components/Footer';
import Header from './components/Header';

import './i18n';
import BlogManagement from './pages/admin/BlogManagement';
import ContactManagement from './pages/admin/ContactManagement';
import WhatsAppButton from './components/WhatsAppButton';

const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [rtlPlugin],
});

const cacheLtr = createCache({
  key: 'muiltr',
});

// Add ScrollToTop component before AppContent
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [pathname]);

  return null;
}

function LoadingBackdrop() {
  return (
    <Backdrop
      sx={{
        color: '#fff',
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
      }}
      open={true}
    >
      <CircularProgress color='inherit' />
    </Backdrop>
  );
}

function AppContent() {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const currentTheme = React.useMemo(
    () => createTheme(theme(isRTL ? 'rtl' : 'ltr')),
    [isRTL]
  );

  React.useEffect(() => {
    document.dir = isRTL ? 'rtl' : 'ltr';
    document.body.style.direction = isRTL ? 'rtl' : 'ltr';
  }, [isRTL]);

  React.useEffect(() => {
    const savedLanguage = localStorage.getItem('i18nextLng');
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      <AuthProvider>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            direction: isRTL ? 'rtl' : 'ltr',
          }}
        >
          <Routes>
            <Route
              path='/*'
              element={
                <>
                  <Header />
                  <div style={{ flexGrow: 1 }}>
                    <Suspense fallback={<LoadingBackdrop />}>
                      <Routes>{renderRoutes()}</Routes>
                      <WhatsAppButton />
                    </Suspense>
                  </div>
                  <Footer />
                </>
              }
            />

            <Route
              path='/login'
              element={
                <Suspense fallback={<LoadingBackdrop />}>
                  <LoginPage />
                </Suspense>
              }
            />

            <Route
              path='/admin/*'
              element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminLayout>
                    <Suspense fallback={<LoadingBackdrop />}>
                      <Routes>
                        <Route
                          path='/'
                          element={<Navigate to='/admin/products' replace />}
                        />
                        <Route path='/products' element={<AdminProducts />} />
                        <Route path='/blogs' element={<BlogManagement />} />
                        <Route
                          path='/contacts'
                          element={<ContactManagement />}
                        />
                      </Routes>
                    </Suspense>
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </div>
      </AuthProvider>
    </ThemeProvider>
  );
}

function App() {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  return (
    <StyledEngineProvider injectFirst>
      <CacheProvider value={isRTL ? cacheRtl : cacheLtr}>
        <Router>
          <ScrollToTop />
          <AppContent />
        </Router>
      </CacheProvider>
    </StyledEngineProvider>
  );
}

export default App;
